import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { Button, ButtonRow, Muted } from '../Controls';
import { EnergyCost } from '../Energy';
import SelectPile from '../SelectPile';
import { EnergySymbol, energyName } from '../Energy';
import {
  CHOOSES_DEFENDING_ATTACK, CHOOSES_DEFENDING_ENERGY, CHOOSES_ENERGY_TYPE, CHOOSES_OPPONENT_BENCH, defendingAttacks,
} from './choices';
import { MoveLabel, MoveList, MoveName } from './styled';

/** Amnesia (to block) or Metronome (to copy): one of the Defending Pokémon's attacks, chosen from a list, then Attack. */
const DefendingAttackPicker = ({ move, opponent, onPick, onBack }) => {
  const [chosenId, setChosenId] = useState(null);
  const defenderName = opponent.getIn(['activePokemon', 'card', 'name'], 'the Defending Pokémon');

  return (
    <>
      <p>Choose 1 of {defenderName}'s attacks for {move.get('name')}. {move.get('text')} Attacking ends your turn.</p>
      <MoveList role="group" aria-label={`${defenderName}'s attacks`}>
        {defendingAttacks(opponent).map(attack => (
          <Button
            key={attack.get('id')}
            onClick={() => setChosenId(attack.get('id'))}
            $primary={chosenId === attack.get('id')}
            $block
            aria-pressed={chosenId === attack.get('id')}
          >
            <MoveLabel>
              <EnergyCost types={attack.get('cost').toJS()} />
              <MoveName>{attack.get('name')}</MoveName>
            </MoveLabel>
          </Button>
        ))}
      </MoveList>
      <ButtonRow style={{ marginTop: 16 }}>
        <Muted>{chosenId ? '1 of 1 chosen' : 'None chosen'}</Muted>
        <Button $primary disabled={!chosenId} onClick={() => onPick({ defendingMoveId: chosenId })}>Attack</Button>
      </ButtonRow>
      <Button onClick={onBack} $block>Back to attacks</Button>
    </>
  );
};

DefendingAttackPicker.propTypes = {
  move: PropTypes.instanceOf(Map).isRequired,
  opponent: PropTypes.instanceOf(Map).isRequired,
  onPick: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

/** Conversion 1 and 2: a type of energy, from the types the move lists. Picking one attacks. */
const EnergyTypePicker = ({ move, onPick, onBack }) => (
  <>
    <p>Choose a type for {move.get('name')}. {move.get('text')} Attacking ends your turn.</p>
    <ButtonRow role="group" aria-label="Energy types" style={{ flexWrap: 'wrap' }}>
      {move.get('energy_types', List()).map(type => (
        <Button key={type} onClick={() => onPick({ energyType: type })}>
          <EnergySymbol type={type} size="1.3em" /> {energyName(type)}
        </Button>
      ))}
    </ButtonRow>
    <Button onClick={onBack} $block>Back to attacks</Button>
  </>
);

EnergyTypePicker.propTypes = {
  move: PropTypes.instanceOf(Map).isRequired,
  onPick: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

/**
 * What an attack asks the player to pick before it goes: one of the Defending Pokémon's energy cards (Whirlpool),
 * one of the opponent's benched Pokémon (Lure), one of the Defending Pokémon's attacks (Amnesia, Metronome) or a type
 * of energy (Conversion). Picking one attacks.
 */
const AttackPicker = ({ move, kind, opponent, onPick, onBack }) => {
  if (kind === CHOOSES_DEFENDING_ATTACK) {
    return <DefendingAttackPicker move={move} opponent={opponent} onPick={onPick} onBack={onBack} />;
  }

  if (kind === CHOOSES_ENERGY_TYPE) {
    return <EnergyTypePicker move={move} onPick={onPick} onBack={onBack} />;
  }

  const defenderName = opponent.getIn(['activePokemon', 'card', 'name'], 'the Defending Pokémon');
  const benched = opponent.getIn(['bench', 'cards'], List());

  const pickers = {
    [CHOOSES_DEFENDING_ENERGY]: {
      cards: opponent.getIn(['activePokemon', 'energyAttachments'], List()),
      message: `Choose 1 of ${defenderName}'s energy cards for ${move.get('name')} to discard.`,
      picks: id => ({ energyAttachmentId: id }),
    },
    [CHOOSES_OPPONENT_BENCH]: {
      cards: benched,
      message: `Choose 1 of your opponent's benched Pokémon for ${move.get('name')} to switch in for ${defenderName}.`,
      picks: id => ({ benchPosition: benched.find(card => card.get('pivot_id') === id).get('order') }),
    },
  };
  const picker = pickers[kind];

  return (
    <>
      <SelectPile
        cardsOrCardables={picker.cards}
        message={`${picker.message} Attacking ends your turn.`}
        selectCount={1}
        selectedIds={List()}
        doneLabel="Attack"
        onSelectDone={ids => onPick(picker.picks(ids.first()))}
      />
      <Button onClick={onBack} $block>Back to attacks</Button>
    </>
  );
};

AttackPicker.propTypes = {
  move: PropTypes.instanceOf(Map).isRequired,
  kind: PropTypes.oneOf([CHOOSES_DEFENDING_ENERGY, CHOOSES_OPPONENT_BENCH, CHOOSES_DEFENDING_ATTACK, CHOOSES_ENERGY_TYPE]).isRequired,
  /** The opponent's activePokemon and bench, from selectOpponent. */
  opponent: PropTypes.instanceOf(Map).isRequired,
  onPick: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AttackPicker;
